import { ChevronDown } from 'lucide-react';
import type { DebugEventRowProps } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/DebugTab/DebugEventRow/index.types';
import { EventsBadge } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/EventsTab/EventsBadge';
import { formatTimestamp } from 'Modules/softphone/components/SoftphoneDevTools/utils';
import React from 'react';
import { cn } from '~/lib/utils';

/**
 * DebugEventRow - One captured event, opening into its raw payload
 *
 * The same row wherever a record lists events, including the device-change
 * timeline.
 */
export const DebugEventRow: React.FC<DebugEventRowProps> = ({
  event,
  relativeLabel,
  badge,
  isOpen,
  onToggle,
}) => (
  <div className="border-b border-zinc-800 last:border-b-0">
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={isOpen}
      disabled={!event.details}
      className="flex w-full items-center gap-[9px] px-[11px] py-2 text-left transition-colors hover:bg-zinc-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-emerald-400/40 disabled:cursor-default disabled:hover:bg-transparent"
    >
      {badge ?? <EventsBadge type={event.type} />}

      <span className="min-w-0 flex-1 truncate font-sp-mono text-xs text-zinc-200">
        {event.message}
      </span>

      {relativeLabel && (
        <span className="flex-shrink-0 font-sp-mono text-[10px] text-emerald-400">
          {relativeLabel}
        </span>
      )}

      <span className="flex-shrink-0 font-sp-mono text-[10.5px] text-zinc-400">
        {formatTimestamp(event.timestamp, { fallback: '—' })}
      </span>

      <ChevronDown
        className={cn(
          'h-3.5 w-3.5 flex-shrink-0 text-zinc-400 transition-transform',
          isOpen && 'rotate-180',
          !event.details && 'invisible'
        )}
      />
    </button>

    {isOpen && event.details && (
      <pre className="max-h-[200px] overflow-auto border-t border-zinc-800 bg-zinc-950 px-3 py-2.5 font-sp-mono text-[11px] leading-[1.6] text-zinc-300">
        {JSON.stringify(event.details, null, 2)}
      </pre>
    )}
  </div>
);
